import { createMemo } from "solid-js";
import { createStore, produce, unwrap } from "solid-js/store";
import type { JobCategory } from "@/domains/job-offer/create/constants";
import type { TaxonomyLookup } from "../store/taxonomy-store";
import type {
  JobSubcategoryKey,
  PickedSubcategory,
  SubcategoryOrigin,
} from "../types";
import type {
  SectorDraft,
  SectorDraftInput,
  SectorPayload,
  SectorProposal,
  SectorSlots,
  SlotIndex,
} from "./types";

export type {
  SectorDraft,
  SectorDraftInput,
  SectorSlot,
  SectorSlots,
  SlotIndex,
} from "./types";

export const MAX_CATEGORIES = 2;
export const MAX_SUBCATEGORIES = 3;

const clone = <T>(value: T): T => structuredClone(value);

const payloadOf = (slots: SectorSlots): SectorPayload => ({
  job_category: slots[0]?.category ?? null,
  job_category_secondary: slots[1]?.category ?? null,
  job_subcategories: slots.flatMap((slot) =>
    (slot?.subs ?? []).map((sub) => sub.key),
  ),
});

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((key) => b.includes(key));

/**
 * The saved profile as two slots. Normalization:
 * - a secondary with no primary is promoted;
 * - the same sector twice keeps only the primary;
 * - each saved subcategory goes to the slot of its parent (`taxonomy.parentOf`);
 * - a key the tree does not know (`parentOf` is `null`) goes to the primary
 *   slot, so it stays in the payload (the backend answers `400`, AC13) while
 *   `subOptions` never shows it;
 * - a key whose parent is a sector the profile does not have is dropped: the
 *   backend would delete it anyway.
 */
function slotsFromSaved(
  saved: SectorDraftInput["saved"],
  taxonomy: TaxonomyLookup,
): SectorSlots {
  const primary = saved.job_category ?? saved.job_category_secondary;
  const secondary =
    saved.job_category && saved.job_category_secondary !== saved.job_category
      ? saved.job_category_secondary
      : null;
  const slots: SectorSlots = [
    primary ? { category: primary, subs: [] } : null,
    secondary ? { category: secondary, subs: [] } : null,
  ];
  const seen = new Set<JobSubcategoryKey>();
  for (const sub of saved.job_subcategories) {
    if (seen.has(sub.key)) continue;
    seen.add(sub.key);
    const parent = taxonomy.parentOf(sub.key);
    const slot = parent
      ? slots.find((candidate) => candidate?.category === parent)
      : slots[0];
    slot?.subs.push({
      key: sub.key,
      origin: sub.status === "confirmed" ? "confirmed" : "suggested",
    });
  }
  return slots;
}

/**
 * The sector selector's rules as a model with no UI: at most 2 sectors, at
 * most 3 subcategories per sector, removing a sector removes its subcategories,
 * a secondary with no primary becomes the primary, Jev's proposal takes the
 * free slot. The taxonomy comes in as an accessor (D4): the model imports
 * neither the store nor `@/api`.
 *
 * Call it inside a reactive root: `visibleProposal`, `payload` and `summary`
 * are memos.
 */
export function createSectorDraft(
  input: SectorDraftInput,
  taxonomy: TaxonomyLookup,
): SectorDraft {
  const initialSlots = slotsFromSaved(input.saved, taxonomy);
  const [state, setState] = createStore<{ slots: SectorSlots }>({
    slots: clone(initialSlots),
  });

  const proposal = input.proposal;
  const normalizedSaved = payloadOf(initialSlots);
  /** The saved primary: in the profile it is changed, never removed alone (AC4). */
  const savedPrimary = initialSlots[0]?.category ?? null;
  const savedCategories = new Set(
    initialSlots.flatMap((slot) => (slot ? [slot.category] : [])),
  );
  const savedSuggested = initialSlots.flatMap((slot) =>
    (slot?.subs ?? [])
      .filter((sub) => sub.origin === "suggested")
      .map((sub) => sub.key),
  );

  /** The proposal's subcategories the sector really owns, at most 3. */
  const proposalSubs: PickedSubcategory[] = proposal
    ? proposal.subcategories
        .filter((key) => taxonomy.parentOf(key) === proposal.category)
        .slice(0, MAX_SUBCATEGORIES)
        .map((key) => ({ key, origin: "suggested" as const }))
    : [];

  /** Keys that, put back after being removed, come back `suggested`. */
  const suggestedPool = new Set<JobSubcategoryKey>([
    ...savedSuggested,
    ...proposalSubs.map((sub) => sub.key),
  ]);

  const current = (): SectorSlots => clone(unwrap(state.slots));

  /** Replaces both slots; the array identity of `state.slots` stays. */
  const write = (next: SectorSlots) => {
    setState(
      produce((draft) => {
        draft.slots[0] = next[0];
        draft.slots[1] = next[1];
      }),
    );
  };

  const freeIndex = (): SlotIndex | null => {
    if (!state.slots[0]) return 0;
    if (!state.slots[1]) return 1;
    return null;
  };

  const chosenCategories = (): JobCategory[] =>
    state.slots.flatMap((slot) => (slot ? [slot.category] : []));

  const visibleProposal = createMemo((): SectorProposal | null => {
    if (!proposal || freeIndex() === null) return null;
    if (state.slots.some((slot) => slot?.category === proposal.category)) {
      return null;
    }
    return proposal;
  });

  const removeCategory = (index: SlotIndex) => {
    if (!canRemove(index)) return;
    const [primary, secondary] = current();
    // The secondary rises to primary when the primary goes (AC4).
    write(index === 0 ? [secondary, null] : [primary, null]);
  };

  const setCategory = (index: SlotIndex, category: JobCategory | null) => {
    if (!category) {
      removeCategory(index);
      return;
    }
    const slots = current();
    // The secondary never fills while the primary is empty.
    const target: SlotIndex = index === 1 && !slots[0] ? 0 : index;
    const other = slots[target === 0 ? 1 : 0];
    if (other?.category === category) return;
    if (slots[target]?.category === category) return;
    // Picking Jev's sector by hand is the same as accepting it.
    const subs =
      proposal && proposal.category === category ? clone(proposalSubs) : [];
    slots[target] = { category, subs };
    write(slots);
  };

  const swap = () => {
    const [primary, secondary] = current();
    if (!primary || !secondary) return;
    write([secondary, primary]);
  };

  const canRemove = (index: SlotIndex): boolean => {
    if (!state.slots[index]) return false;
    if (index === 1) return true;
    return (
      input.container === "onboarding" || !!state.slots[1] || !savedPrimary
    );
  };

  const isPicked = (index: SlotIndex, key: JobSubcategoryKey) =>
    !!state.slots[index]?.subs.some((sub) => sub.key === key);

  const originOf = (
    index: SlotIndex,
    key: JobSubcategoryKey,
  ): SubcategoryOrigin | null =>
    state.slots[index]?.subs.find((sub) => sub.key === key)?.origin ?? null;

  const isFull = (index: SlotIndex) =>
    (state.slots[index]?.subs.length ?? 0) >= MAX_SUBCATEGORIES;

  const toggleSub = (index: SlotIndex, key: JobSubcategoryKey) => {
    const slot = state.slots[index];
    if (!slot) return;
    if (isPicked(index, key)) {
      setState("slots", index, "subs", (subs) =>
        (subs ?? []).filter((sub) => sub.key !== key),
      );
      return;
    }
    if (isFull(index)) return;
    // Only the sector's own subcategories: anything else is a `400` at save.
    if (taxonomy.parentOf(key) !== slot.category) return;
    const origin: SubcategoryOrigin = suggestedPool.has(key)
      ? "suggested"
      : "manual";
    setState("slots", index, "subs", (subs) => [
      ...(subs ?? []),
      { key, origin },
    ]);
  };

  const snapshot = (): SectorSlots => current();

  const restore = (slots: SectorSlots) => {
    write(clone(slots));
  };

  const acceptProposal = () => {
    const visible = visibleProposal();
    const index = freeIndex();
    if (!visible || index === null) return;
    setCategory(index, visible.category);
  };

  const acceptedProposalPosition = (): "primary" | "secondary" | null => {
    if (!proposal || savedCategories.has(proposal.category)) return null;
    if (state.slots[0]?.category === proposal.category) return "primary";
    if (state.slots[1]?.category === proposal.category) return "secondary";
    return null;
  };

  const payload = createMemo(() => payloadOf(state.slots));

  const summary = createMemo(() => {
    const kept = new Set(payload().job_subcategories);
    const chosen = new Set<string>(chosenCategories());
    const counted = new Set<JobSubcategoryKey>(savedSuggested);
    if (acceptedProposalPosition() !== null) {
      for (const sub of proposalSubs) counted.add(sub.key);
    }
    let confirmed = 0;
    let rejected = 0;
    for (const key of counted) {
      if (kept.has(key)) confirmed += 1;
      // Removing a sector deletes its subcategories: they are not rejected.
      else if (chosen.has(taxonomy.parentOf(key) ?? "")) rejected += 1;
    }
    return { confirmed, rejected };
  });

  const subOptions = (index: SlotIndex) => {
    const slot = state.slots[index];
    if (!slot) return [];
    return taxonomy.subcategoriesOf(slot.category).map((key) => ({
      key,
      origin: originOf(index, key),
    }));
  };

  const isDirty = () => {
    const now = payload();
    return (
      now.job_category !== normalizedSaved.job_category ||
      now.job_category_secondary !== normalizedSaved.job_category_secondary ||
      !sameSet(now.job_subcategories, normalizedSaved.job_subcategories)
    );
  };

  return {
    source: input.source,
    container: input.container,
    get slots() {
      return state.slots;
    },
    freeIndex,
    visibleProposal,
    chosenCategories,
    setCategory,
    removeCategory,
    swap,
    canRemove,
    isPicked,
    originOf,
    isFull,
    toggleSub,
    snapshot,
    restore,
    acceptProposal,
    acceptedProposalPosition,
    payload,
    summary,
    subOptions,
    isDirty,
  };
}
